import { integrationApi } from "@repo/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { getCronExpression } from "../../utils/schedule";

interface UseUpdateGcpIntegrationProps {
  integrationId: number;
  onSuccess: (
    message: string,
    isDraft?: boolean,
    integrationType?: string,
  ) => void;
  onError: (message: string) => void;
}

export function useUpdateGcpIntegration({
  integrationId,
  onSuccess,
  onError,
}: UseUpdateGcpIntegrationProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      isEnabled,
      data,
    }: {
      isEnabled: boolean;
      data: Record<string, unknown>;
    }) => {
      const payload = {
        integrationId,
        displayName: (data.name as string) || "",
        isEnabled,
        credentials: {
          type: "GCP_SERVICE_ACCOUNT",
          data: {
            service_account_json: (data.service_account_key as string) || "",
          },
        },
        metadata: {
          accountId: (data.project_id as string) || "",
          description: (data.description as string) || "",
        },
        features: ((data.featureNames as string[]) || ["POSTURE_FOR_GCP"]).map(
          (featureName) => ({
            featureName,
            schedules: [
              {
                scheduleName: "gcp-schedule",
                cronExpression: getCronExpression(
                  (data.frequency as string) || "24hours",
                  data.importTime as Date | null,
                ),
              },
            ],
          }),
        ),
      };
      return integrationApi.updateIntegration(integrationId, payload);
    },
    onError: () => {
      onError(
        intl.formatMessage({
          id: "M54/cu",
          defaultMessage: "Failed to update integration",
          description: "Error message for integration update failure",
        }),
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      queryClient.invalidateQueries({
        queryKey: ["integration", integrationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["integrationCredentials", integrationId],
      });
      const isDraft = !variables.isEnabled;
      if (isDraft) {
        onSuccess(
          intl.formatMessage(
            {
              id: "c2bz4O",
              defaultMessage:
                "You can resume the setup of {integrationType} at any time.",
              description: "Draft save success message",
            },
            { integrationType: variables.data.name as string },
          ),
          true,
          variables.data.name as string,
        );
      } else {
        onSuccess(
          intl.formatMessage({
            id: "1rvQ3c",
            defaultMessage: "Integration updated successfully",
            description: "Success message for integration update",
          }),
          false,
        );
      }
    },
  });
}
